import { DeleteOutlineOutlined } from '@mui/icons-material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import moment, { Moment } from 'moment';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked';
import { FlagRemovalType } from '../../../shared/services/reduction-action/reduction-action.model';
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service';
import { IReportPeriod } from '../../../shared/services/report-period/report-period.model';
import ReportPeriodService from '../../../shared/services/report-period/report-period.service';
import { IUserDetails } from '../../../shared/services/user-details/user-details.model';
import UserDetailsService from '../../../shared/services/user-details/user-details.service';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';

const REMOVAL_TYPE_OPTIONS: { value: FlagRemovalType; label: string }[] = [
  { value: 'afforestation_reforestation', label: 'Afforestation / Reforestation' },
  { value: 'soil_carbon', label: 'Soil Carbon' },
  { value: 'peatland_wetland_restoration', label: 'Peatland / Wetland Restoration' },
  { value: 'agroforestry', label: 'Agroforestry' },
  { value: 'other_land_based_removal', label: 'Other Land-Based Removal' },
];

export const FlagRemovalActionDialog: FunctionComponent<{
  open: boolean;
  actionId?: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, actionId, onClose, onSaved }) => {
  const reductionActionService = new ReductionActionService();
  const userDetailsService = new UserDetailsService();
  const reportPeriodService = new ReportPeriodService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);
  const isReportPeriodLocked = useIsReportPeriodLocked();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openDate, setOpenDate] = useState(false);
  const [users, setUsers] = useState<IUserDetails[]>([]);
  const [reportPeriods, setReportPeriods] = useState<IReportPeriod[]>([]);
  const [reportPeriodReductionActionId, setReportPeriodReductionActionId] = useState<string | null>(null);
  const [linkedReductionActionId, setLinkedReductionActionId] = useState<string | null>(null);
  const [linkedReportPeriodId, setLinkedReportPeriodId] = useState<string | null>(null);
  const [isLinkedPeriodLocked, setIsLinkedPeriodLocked] = useState(false);

  const isEditMode = !!actionId;

  const formik = useFormik({
    initialValues: {
      reportPeriodId: reportPeriodContext.lastSelectedOpenReportId || reportPeriodContext.lastSelectedReportId || '',
      name: '',
      description: '',
      removalType: '' as FlagRemovalType | '',
      status: 'Not Started',
      dueDate: '',
      userId: '',
      annualReduction: '' as number | '',
      reductionStartReportingPeriodId: '',
      reductionLifetimePeriods: '' as number | '',
    },
    validationSchema: Yup.object().shape({
      reportPeriodId: Yup.string().required('Reporting period is required'),
      name: Yup.string().required('Name is required'),
      description: Yup.string().nullable(),
      removalType: Yup.string().required('Removal type is required'),
      status: Yup.string().nullable(),
      dueDate: Yup.string().nullable(),
      userId: Yup.string().nullable(),
      annualReduction: Yup.number().min(0, 'Annual removal must be 0 or greater').nullable(),
      reductionStartReportingPeriodId: Yup.string().nullable(),
      reductionLifetimePeriods: Yup.number().integer('Must be a whole number').min(1, 'Must be at least 1 period').nullable(),
    }),
    onSubmit: async (values) => {
      try {
        if (isSubmitting) return;
        setIsSubmitting(true);

        const planningPayload = {
          status: values.status,
          dueDate: values.dueDate || null,
          userId: values.userId || null,
          annualReduction: values.annualReduction === '' ? null : Number(values.annualReduction) * 1000,
          reductionStartReportingPeriodId: values.reductionStartReportingPeriodId || null,
          reductionLifetimePeriods: values.reductionLifetimePeriods === '' ? null : values.reductionLifetimePeriods,
        };

        if (isEditMode && linkedReductionActionId && reportPeriodReductionActionId) {
          await reductionActionService.editReductionAction(linkedReductionActionId, {
            name: values.name,
            description: values.description,
            actionType: 'flag_removal',
            removalType: (values.removalType || null) as FlagRemovalType | null,
            groupId: '',
            categoryId: [],
            icon: 'starOutlined',
          });
          await reductionActionService.editReductionActionForReportPeriod(reportPeriodReductionActionId, planningPayload);
        } else {
          const createdAction = await reductionActionService.createReductionAction({
            name: values.name,
            description: values.description,
            actionType: 'flag_removal',
            removalType: (values.removalType || null) as FlagRemovalType | null,
            isCustom: true,
            groupId: '',
            categoryId: [],
            icon: 'starOutlined',
          });
          const addResponse = await reductionActionService.addReductionActionToReportPeriod({
            reductionActionId: createdAction.id,
            reportPeriodId: values.reportPeriodId,
          });
          if (addResponse?.reportPeriodReductionActionId) {
            await reductionActionService.editReductionActionForReportPeriod(addResponse.reportPeriodReductionActionId, planningPayload);
          }
        }

        onSaved();
        onClose();
      } catch (err: any) {
        if (err?.response?.data?.code === 1) {
          userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
        } else {
          const message = err?.response?.data?.message || 'There was an error, please try again';
          userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    userDetailsService
      .getAllUserDetails('?status=active')
      .then((response) => setUsers(response.filter((user) => !(user.inviteSent === true && user.inviteAccepted === false))))
      .catch((err) => console.log(err));

    reportPeriodService
      .getAllReportPeriod()
      .then((response) => {
        const sorted = response.sort((a, b) => Number(a.calendarYear) - Number(b.calendarYear));
        setReportPeriods(sorted);
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!isEditMode || !actionId) {
      setReportPeriodReductionActionId(null);
      setLinkedReductionActionId(null);
      setLinkedReportPeriodId(null);
      setIsLinkedPeriodLocked(false);
      formik.resetForm({
        values: {
          ...formik.initialValues,
          reportPeriodId: reportPeriodContext.lastSelectedOpenReportId || reportPeriodContext.lastSelectedReportId || '',
        },
      });
      return;
    }

    reductionActionService
      .getReportPeriodReductionAction(actionId, 'actionType=flag_removal')
      .then((response) => {
        setReportPeriodReductionActionId(response.reportPeriodReductionAction.id);
        setLinkedReductionActionId(response.id);
        setLinkedReportPeriodId(response.reportPeriodReductionAction.reportPeriodId);
        setIsLinkedPeriodLocked(response.reportPeriodReductionAction.reportPeriod?.locked === true);
        formik.setValues({
          reportPeriodId: response.reportPeriodReductionAction.reportPeriodId || '',
          name: response.name || '',
          description: response.description || '',
          removalType: (response.removalType || '') as FlagRemovalType | '',
          status: response.reportPeriodReductionAction.inProgress
            ? 'In Progress'
            : response.reportPeriodReductionAction.isCompleted
              ? 'Completed'
              : 'Not Started',
          dueDate: response.reportPeriodReductionAction.dueDate
            ? moment(response.reportPeriodReductionAction.dueDate).format('YYYY-MM-DD')
            : '',
          userId: response.reportPeriodReductionAction.userId || '',
          annualReduction:
            response.reportPeriodReductionAction.annualReduction != null
              ? Number(response.reportPeriodReductionAction.annualReduction) / 1000
              : '',
          reductionStartReportingPeriodId: response.reportPeriodReductionAction.reductionStartReportingPeriodId || '',
          reductionLifetimePeriods: response.reportPeriodReductionAction.reductionLifetimePeriods ?? '',
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, [open, isEditMode, actionId, reportPeriodContext.lastSelectedOpenReportId, reportPeriodContext.lastSelectedReportId]);

  const isLocked = (isEditMode ? isLinkedPeriodLocked : isReportPeriodLocked === true) || false;

  const deleteHandler = async () => {
    if (!linkedReductionActionId || !linkedReportPeriodId) return;
    userNotificationsContext
      .openDialog({
        active: true,
        title: 'Confirmation',
        message: 'Are you sure you want to delete this FLAG removal action? This action cannot be undone.',
        buttonCopy: 'Continue',
        cancelButtonCopy: 'Cancel',
      })
      .then(async (response) => {
        if (!response) return;
        await reductionActionService.deleteReductionActionToReportPeriod(linkedReductionActionId, linkedReportPeriodId);
        onSaved();
        onClose();
      })
      .catch((err) => console.log(err));
  };

  const title = isEditMode ? 'Edit FLAG Removal Action' : 'Add FLAG Removal Action';

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={title}
      cancelHandler={onClose}
      submitHandler={formik.submitForm}
      isSubmitting={isSubmitting}
      disableSaveButton={isLocked}
      disableCancelButton={false}
      maxWidth='sm'
    >
      <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
        <InputLabel htmlFor='name'>Removal Name</InputLabel>
        <OutlinedInput
          id='name'
          name='name'
          label='Removal Name'
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && Boolean(formik.errors.name)}
        />
        {formik.touched.name && formik.errors.name && (
          <FormHelperText sx={{ color: '#bf3333' }}>{formik.errors.name}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='description'>Description</InputLabel>
        <OutlinedInput
          id='description'
          name='description'
          label='Description'
          value={formik.values.description}
          onChange={formik.handleChange}
          error={formik.touched.description && Boolean(formik.errors.description)}
        />
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='removalType'>Removal Type</InputLabel>
        <Select
          id='removalType'
          name='removalType'
          label='Removal Type'
          value={formik.values.removalType}
          onChange={formik.handleChange}
          error={formik.touched.removalType && Boolean(formik.errors.removalType)}
        >
          {REMOVAL_TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        {formik.touched.removalType && formik.errors.removalType && (
          <FormHelperText sx={{ color: '#bf3333' }}>{formik.errors.removalType as string}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='reportPeriodId'>Reporting Period</InputLabel>
        <Select
          id='reportPeriodId'
          name='reportPeriodId'
          label='Reporting Period'
          value={formik.values.reportPeriodId}
          onChange={formik.handleChange}
          disabled={isEditMode}
          error={formik.touched.reportPeriodId && Boolean(formik.errors.reportPeriodId)}
        >
          {reportPeriods.map((period) => (
            <MenuItem key={period.id} value={period.id}>{period.calendarYear}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      <Typography variant='subtitle2' sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>Delivery</Typography>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='userId'>Owner</InputLabel>
        <Select
          value={formik.values.userId || ''}
          onChange={(e: SelectChangeEvent<string>) => formik.setFieldValue('userId', e.target.value)}
          id='userId'
          name='userId'
          label='Owner'
        >
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>{`${user.firstName} ${user.surname}`}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label='Due Date'
            format='DD/MM/YYYY'
            open={openDate}
            value={formik.values.dueDate ? moment(formik.values.dueDate, 'YYYY-MM-DD') : null}
            onChange={(date: Moment | null) => {
              formik.setFieldValue('dueDate', date ? date.format('YYYY-MM-DD') : '');
            }}
            onOpen={() => setOpenDate(true)}
            onClose={() => setOpenDate(false)}
            slotProps={{
              textField: { onClick: () => setOpenDate(true) },
            }}
            minDate={moment()}
          />
        </LocalizationProvider>
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='status'>Status</InputLabel>
        <Select
          value={formik.values.status || ''}
          onChange={formik.handleChange}
          id='status'
          name='status'
          label='Status'
        >
          <MenuItem value='Not Started'>Not Started</MenuItem>
          <MenuItem value='In Progress'>In Progress</MenuItem>
          <MenuItem value='Completed'>Completed</MenuItem>
        </Select>
      </FormControl>

      <Divider sx={{ my: 3 }} />
      <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>Projection Impact</Typography>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='annualReduction'>Projected Annual Removal</InputLabel>
        <OutlinedInput
          id='annualReduction'
          name='annualReduction'
          label='Projected Annual Removal'
          type='number'
          value={formik.values.annualReduction}
          onChange={formik.handleChange}
          endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
          inputProps={{ step: '0.01', min: 0 }}
          error={formik.touched.annualReduction && Boolean(formik.errors.annualReduction)}
        />
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='reductionStartReportingPeriodId'>Start Reporting Period</InputLabel>
        <Select
          value={formik.values.reductionStartReportingPeriodId || ''}
          onChange={(e: SelectChangeEvent<string>) => formik.setFieldValue('reductionStartReportingPeriodId', e.target.value || null)}
          id='reductionStartReportingPeriodId'
          name='reductionStartReportingPeriodId'
          label='Start Reporting Period'
        >
          <MenuItem value=''>
            <Typography sx={{ color: 'text.secondary' }}>Select period</Typography>
          </MenuItem>
          {reportPeriods.map((period) => (
            <MenuItem key={period.id} value={period.id}>{period.calendarYear}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='reductionLifetimePeriods'>Removal Lifetime (reporting periods)</InputLabel>
        <OutlinedInput
          id='reductionLifetimePeriods'
          name='reductionLifetimePeriods'
          label='Removal Lifetime (reporting periods)'
          type='number'
          value={formik.values.reductionLifetimePeriods}
          onChange={formik.handleChange}
          inputProps={{ step: 1, min: 1 }}
          placeholder='Ongoing'
          error={formik.touched.reductionLifetimePeriods && Boolean(formik.errors.reductionLifetimePeriods)}
        />
        <FormHelperText>Number of reporting periods this removal is active. Leave blank if ongoing.</FormHelperText>
      </FormControl>

      {isEditMode && (
        <>
          <Divider sx={{ my: 3 }} />
          <Button
            variant='text'
            color='error'
            onClick={deleteHandler}
            disabled={isLocked}
            startIcon={<DeleteOutlineOutlined />}
            sx={{ textTransform: 'none' }}
          >
            Remove from Plan
          </Button>
        </>
      )}
    </DialogWrapper>
  );
};
